import { alpha, Box, Card, Chip, styled, Switch, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, tooltipClasses, Typography } from '@mui/material';
import { green } from '@mui/material/colors';
import dayjs from 'dayjs';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateAvailablity } from '../../redux/Calendar/action';
import AppointmentHoverDialog from './AppointmentHoverDialog';
import { useQueryClient } from '@tanstack/react-query';
import { coreSettingsKeys } from '../../services/core/settings';

const RedSwitch = styled(Switch)(({ theme }) => ({
    '& .MuiSwitch-switchBase.Mui-checked': {
        color: green[800],
        '&:hover': {
            backgroundColor: alpha(green[800], theme.palette.action.hoverOpacity),
        },
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: green[800],
    },
}));

const styles = {
    table: {
        minWidth: 650,
        borderCollapse: 'collapse',
        border: '1px solid #D8D8D8',
    },
    th: {
        border: '1px solid #D8D8D8',
        borderRight: 'none',
        borderBottom: '1px solid #D8D8D8',
        padding: '0px',
        textAlign: 'center',
    },
    tdHeading: {
        border: '1px solid #D8D8D8',
        backgroundColor: '#ECEFF1',
        borderLeft: '0px',
        borderRight: 'none',
        borderTop: 'none',
        padding: '8px',
        height: '100px',
        textAlign: 'center',
        alignItem: 'end',
    },
    tdSlotHeading: {
        borderLeft: '1px solid #D8D8D8',
        borderBottom: '0px',
        padding: '8px',
        paddingRight: '0px',
        height: '100px',
        textAlign: 'end',
        alignItem: 'end',
        display: 'grid',
        alignContent: 'end',
        minWidth: 'max-Content',
    },
    td: {
        border: '1px solid #D8D8D8',
        borderTop: 'none',
        padding: '8px',
        textAlign: 'center',
        borderRight: 'none',
    },
    oneCard: {
        paddingLeft: '7px',
        marginTop: '-12px',
    },
    slotTime: {
        display: 'flex',
        justifyContent: 'end',
        alignItems: 'center',
    },
};

export const borderColor = (status) => {
    if (status == 'waiting_for_drop_off') return '8px solid #75DBFF';
    else if (status == 'vehicle_dropped_off') return '8px solid #FFC30F';
    else if (status == 'in_progress') return '8px solid #FF9E31';
    else if (status == 'ready_for_pick_up') return '8px solid #5CE469';
    else if (status == 'customer_paid') return '8px solid #00CD44';
    else return '8px solid #F44336';
};

export const backgroundColor = (status) => {
    if (status == 'waiting_for_drop_off') return '#C1EFFF';
    else if (status == 'vehicle_dropped_off') return '#FFF0C2';
    else if (status == 'in_progress') return '#FFE6CB';
    else if (status == 'ready_for_pick_up') return '#F0F0F0';
    else if (status == 'customer_paid') return '#CEFFDE';
    else return '#FDE2E0';
};

const CustomCalendar = (props) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const currentDate = new Date(props.date);
    const [dayLimit, setDayLimit] = useState(6);
    const [slotData, setSlotData] = useState([]);
    const defaultCalenderRef = useRef([]);

    // Hover dialog state
    const [hoverDialog, setHoverDialog] = useState({
        open: false,
        appointment: null,
        position: { x: 0, y: 0 },
    });

    // Timeout ref for mouse leave delay
    const mouseLeaveTimeoutRef = useRef(null);
    const measuredDialogHeightRef = useRef(null);
    const lastTriggerRectRef = useRef(null);
    const currentHoveredAppointmentRef = useRef(null);

    const OFFSET = 8;
    const clamp = (value, min, max) => Math.max(min, Math.min(value, max));
    const computeFixedPosition = (rect, dialogWidth, dialogHeight) => {
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        let y;
        if (spaceBelow >= dialogHeight + OFFSET) {
            y = rect.bottom + OFFSET;
        } else if (spaceAbove >= dialogHeight + OFFSET) {
            y = rect.top - dialogHeight - OFFSET;
        } else {
            const idealBelow = rect.bottom + OFFSET;
            y = clamp(idealBelow, 8, window.innerHeight - dialogHeight - 8);
        }

        let x = rect.left + rect.width / 2 - dialogWidth / 2;
        x = clamp(x, 8, window.innerWidth - dialogWidth - 8);
        return { x, y };
    };

    const currentDayOfWeek = currentDate.getDay();
    const startDate = new Date(currentDate);
    const endDate = new Date(currentDate);

    const daysInWeek = [];
    const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

    for (let i = 0; i < dayLimit - 1; i++) {
        const day = new Date(startDate);
        day.setDate(startDate.getDate() + i);
        daysInWeek.push({
            date: day,
            dayName: dayNames[day.getDay()],
        });
    }

    const [data, setData] = useState([]);
    useEffect(() => {
        let d = [];
        props.resources?.map((res) => {
            d.push({
                name: res?.title,
                appointment: props.events.filter((e) => e.resourceId == res._id),
            });
        });
        setData(d);
    }, [props?.resources, props.events]);

    useEffect(() => {
        if (props?.view == '3day') {
            setDayLimit(4);
        } else {
            setDayLimit(6);
        }
    }, [props?.view]);

    useEffect(() => {
        const timeSlots = [];

        for (let hour = 0; hour <= 23; hour++) {
            for (let minute = 0; minute <= 30; minute += 30) {
                const time = new Date(0, 0, 0, hour, minute);
                const formattedTime = time.toLocaleString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                });
                timeSlots.push({
                    time: formattedTime,
                    key: timeSlots.length,
                    appointment: [
                        [
                            {
                                number: '',
                                customerName: '',
                                service: '',
                                dropOffTime: '',
                                height: '',
                                id: '',
                                appointmentStatus: '',
                                width: '',
                                hasMargin: false,
                                margin: '',
                            },
                        ],
                        [
                            {
                                number: '',
                                customerName: '',
                                service: '',
                                dropOffTime: '',
                                height: '',
                                id: '',
                                appointmentStatus: '',
                                width: '',
                                hasMargin: false,
                                margin: '',
                            },
                        ],
                        [
                            {
                                number: '',
                                customerName: '',
                                service: '',
                                dropOffTime: '',
                                height: '',
                                id: '',
                                appointmentStatus: '',
                                width: '',
                                hasMargin: false,
                                margin: '',
                            },
                        ],
                        [
                            {
                                number: '',
                                customerName: '',
                                service: '',
                                dropOffTime: '',
                                height: '',
                                id: '',
                                appointmentStatus: '',
                                width: '',
                                hasMargin: false,
                                margin: '',
                            },
                        ],
                        [
                            {
                                number: '',
                                customerName: '',
                                service: '',
                                dropOffTime: '',
                                height: '',
                                id: '',
                                appointmentStatus: '',
                                width: '',
                                hasMargin: false,
                                margin: '',
                            },
                        ],
                    ],
                });
            }

            if (timeSlots.length == 48) {
                let allData = [...timeSlots];

                props.events?.map((event) => {
                    let foundColumn = daysInWeek.findIndex((d) => new Date(d.date).getDate() == new Date(event.start).getDate());
                    let date = new Date(event.start);
                    let updatedDate = new Date(event.start);

                    let marginRequired = false;
                    let marginSize = '';
                    if (new Date(date).getMinutes() < 30 && new Date(date).getMinutes() !== 0) {
                        updatedDate = new Date(`${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()} ${date.getHours()}:00 `);
                        marginRequired = true;
                        if (new Date(date).getMinutes() < 15) {
                            marginSize = '-' + (15 - new Date(date).getMinutes()) * 3;
                        } else if (new Date(date).getMinutes() > 15) {
                            marginSize = (new Date(date).getMinutes() - 15) * 3;
                        } else {
                            marginSize = 0;
                        }
                    } else if (new Date(date).getMinutes() > 30 && new Date(date).getMinutes() !== 0) {
                        updatedDate = new Date(`${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()} ${date.getHours()}:30 `);
                        marginRequired = true;
                        if (30 - (60 - new Date(date).getMinutes()) < 15) {
                            marginSize = '-' + (15 - (30 - (60 - new Date(date).getMinutes()))) * 3;
                        } else if (30 - (60 - new Date(date).getMinutes()) > 15) {
                            marginSize = (30 - (60 - new Date(date).getMinutes()) - 15) * 3;
                        } else {
                            marginSize = 0;
                        }
                    } else {
                        updatedDate = updatedDate;
                        marginRequired = false;
                    }

                    const options = {
                        hour: 'numeric',
                        minute: 'numeric',
                        hour12: true,
                    };
                    const time12HourFormat = updatedDate.toLocaleTimeString('en-US', options);
                    const actualTime12HourFormat = date.toLocaleTimeString('en-US', options);

                    let findRowIndex = timeSlots.findIndex((d) => d.time == time12HourFormat);

                    const endTime = new Date(event.end);
                    let findSlot = '';

                    let endHour = endTime.getHours();
                    const endMinute = endTime.getMinutes();
                    const isPM = endHour >= 12;

                    if (endHour > 12) {
                        endHour -= 12;
                    } else if (endHour === 0) {
                        endHour = 12;
                    }

                    if (endMinute > 30) {
                        findSlot += `${endHour}:30 ${isPM ? 'PM' : 'AM'}`;
                    } else {
                        findSlot += `${endHour}:00 ${isPM ? 'PM' : 'AM'}`;
                    }
                    let findEndRowIndex = timeSlots.findIndex((d) => d.time == findSlot);
                    let differnce = findEndRowIndex - findRowIndex;
                    let minuteDifferce = endMinute <= 30 ? endMinute / 30 : (endMinute - 30) / 30;

                    if (findRowIndex >= 0 && foundColumn >= 0 && event.resourceId == props.resources[0].id) {
                        let appLength = allData[findRowIndex + 1].appointment[foundColumn];

                        allData[findRowIndex + 1].appointment[foundColumn][appLength.length - 1].number = event.title;
                        allData[findRowIndex + 1].appointment[foundColumn][appLength.length - 1].customerName =
                            event.customer?.firstName + ' ' + (event?.customer?.lastName ?? '');
                        allData[findRowIndex + 1].appointment[foundColumn][appLength.length - 1].service = event.service;
                        allData[findRowIndex + 1].appointment[foundColumn][appLength.length - 1].id = event.appointmentId;
                        allData[findRowIndex + 1].appointment[foundColumn][appLength.length - 1].dropOffTime = actualTime12HourFormat;
                        allData[findRowIndex + 1].appointment[foundColumn][appLength.length - 1].height =
                            (differnce + minuteDifferce) * 100 + (marginRequired ? -100 + (marginSize > 0 ? marginSize : -marginSize) : -0);
                        allData[findRowIndex + 1].appointment[foundColumn][appLength.length - 1].appointmentStatus = event.status;
                        allData[findRowIndex + 1].appointment[foundColumn][appLength.length - 1].hasMargin = marginRequired;
                        allData[findRowIndex + 1].appointment[foundColumn][appLength.length - 1].margin = +marginSize + 'px';

                        appLength.push({
                            number: '',
                            customerName: '',
                            service: '',
                            dropOffTime: '',
                            height: '',
                            id: '',
                            appointmentStatus: '',
                            width: '',
                            hasMargin: false,
                            margin: '',
                        });
                    }
                });

                setSlotData(allData);
                props.setShowLoader(false);
            }
        }
    }, [props.resources, props.events]);

    const addRef = (el, index) => {
        if (el) {
            defaultCalenderRef.current[index] = el;
        }
    };

    const getCurrentTimeIndex = () => {
        const now = new Date();
        const minutes = now.getMinutes();
        const roundedMinutes = minutes < 30 ? 0 : 30;
        const roundedTime = new Date(now);
        roundedTime.setMinutes(roundedMinutes, 0, 0);

        const currentTime = roundedTime.toLocaleString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });

        const currentIndex = slotData.findIndex((slot) => slot.time === currentTime);
        return currentIndex >= 0 ? currentIndex : 0;
    };

    const scrollToRef = (index) => {
        if (defaultCalenderRef.current[index]) {
            defaultCalenderRef.current[index].scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    };

    const updateStatusAvailablity = async (payload) => {
        props.setShowLoader(true);
        const resp = await dispatch(updateAvailablity(payload));

        if (resp) {
            queryClient.invalidateQueries({ queryKey: coreSettingsKeys.config(), refetchType: 'all' });
            // Also invalidate the disabled slots list
            queryClient.invalidateQueries({
                queryKey: coreSettingsKeys.slotsWithDisabled.all(),
                exact: false, // Invalidate all queries that start with this key
                refetchType: 'all',
            });
            // Also invalidate the disabled slots list
            queryClient.invalidateQueries({
                queryKey: coreSettingsKeys.disabledSlotsList(),
                refetchType: 'all',
            });
            // Invalidate available dates queries since disabling slots might affect date availability
            queryClient.invalidateQueries({
                queryKey: coreSettingsKeys.availableDates.all(),
                exact: false,
                refetchType: 'all',
            });
            props.fetchAvailablityStatus();
        }
    };

    // Hover handlers with prevention of repeated triggers
    const handleMouseEnter = (event, appointment) => {
        // Prevent repeated triggers for the same appointment
        if (currentHoveredAppointmentRef.current === appointment.appointmentId) {
            return;
        }

        // Clear any pending timeout
        if (mouseLeaveTimeoutRef.current) {
            clearTimeout(mouseLeaveTimeoutRef.current);
            mouseLeaveTimeoutRef.current = null;
        }

        // Update current hovered appointment
        currentHoveredAppointmentRef.current = appointment.appointmentId;

        const rect = event.currentTarget.getBoundingClientRect();
        lastTriggerRectRef.current = rect;

        const dialogWidth = 300;
        const dialogHeight = measuredDialogHeightRef.current ?? 370;
        const { x, y } = computeFixedPosition(rect, dialogWidth, dialogHeight);

        setHoverDialog({ open: true, appointment: appointment, position: { x, y } });
    };

    const handleMouseLeave = () => {
        mouseLeaveTimeoutRef.current = setTimeout(() => {
            setHoverDialog((prev) => ({ ...prev, open: false }));
            currentHoveredAppointmentRef.current = null;
        }, 150);
    };

    const handleDialogMouseEnter = () => {
        if (mouseLeaveTimeoutRef.current) {
            clearTimeout(mouseLeaveTimeoutRef.current);
            mouseLeaveTimeoutRef.current = null;
        }
    };

    const handleDialogMouseLeave = () => {
        setHoverDialog((prev) => ({ ...prev, open: false }));
        currentHoveredAppointmentRef.current = null;
    };

    const handleCloseHoverDialog = () => {
        if (mouseLeaveTimeoutRef.current) {
            clearTimeout(mouseLeaveTimeoutRef.current);
            mouseLeaveTimeoutRef.current = null;
        }
        setHoverDialog((prev) => ({ ...prev, open: false }));
        currentHoveredAppointmentRef.current = null;
    };

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (mouseLeaveTimeoutRef.current) {
                clearTimeout(mouseLeaveTimeoutRef.current);
            }
        };
    }, []);

    const LightTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme }) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: 'transparent',
            color: 'rgba(0, 0, 0, 0.87)',
            boxShadow: theme.shadows[0],
            fontSize: 11,
        },
    }));

    useEffect(() => {
        if (slotData.length > 0) {
            const currentIndex = getCurrentTimeIndex();
            scrollToRef(currentIndex);
        }
    }, [slotData, props?.view]);

    return (
        <div
            style={{
                maxHeight: '110vh',
                position: 'relative',
                overflowY: 'auto',
                zIndex: 0,
            }}
        >
            <Table aria-label="caption table" stickyHeader className="customTable">
                <TableHead>
                    <TableRow>
                        <TableCell
                            style={{
                                ...styles?.th,
                                borderLeft: props?.resources?.length == 1 && '1px solid #D8D8D8',
                                borderRight: '0px solid #D8D8D8',
                            }}
                        >
                            {' '}
                        </TableCell>
                        {Array.isArray(daysInWeek) &&
                            daysInWeek?.map((day, index) => {
                                return (
                                    <TableCell align="center" style={styles?.th} display key={index}>
                                        <Typography className="customCalendarHeaderdayName" fontSize={'13px'}>
                                            {day?.dayName}
                                        </Typography>
                                        <Typography variant="h4" className="customCalendarHeaderdayDate">
                                            {new Date(day.date).getDate()}
                                        </Typography>
                                        <Typography className="customCalendarHeaderdayStatus">
                                            <RedSwitch
                                                checked={
                                                    props.availablity.filter((d) => d.date == moment(day?.date).format('YYYY-MM-DD'))?.length == 0
                                                        ? true
                                                        : false
                                                }
                                                onChange={(e) => {
                                                    updateStatusAvailablity({
                                                        shopId: JSON.parse(localStorage.getItem('apex-saas_details'))?._id,
                                                        date: moment(day?.date).format('YYYY-MM-DD'),
                                                        isTurnOff: !e.target.checked,
                                                    });
                                                }}
                                                size="small"
                                            />
                                            Appointment Requests:{' '}
                                            {props.availablity.filter((d) => d.date == moment(day?.date).format('YYYY-MM-DD'))?.length == 0 ? 'ON' : 'OFF'}
                                        </Typography>
                                    </TableCell>
                                );
                            })}
                    </TableRow>
                </TableHead>
                {props.resources.length > 1 && (
                    <TableBody style={{ height: '100%', overflow: 'auto', position: 'relative' }}>
                        {data.map((row) => (
                            <TableRow key={row.name}>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    style={{
                                        ...styles.tdHeading,
                                        height: 65 / props.resources.length + 'vh',
                                    }}
                                >
                                    <Chip label={row?.name} size="medium" variant="filled" className="customCalendarChip" />
                                </TableCell>
                                {Array.from({ length: dayLimit == 6 ? 5 : 3 }, (_, index) => (
                                    <TableCell
                                        style={styles.td}
                                        key={index}
                                        onClick={() => {
                                            if (hoverDialog.open) {
                                                handleCloseHoverDialog();
                                            }
                                        }}
                                    >
                                        {row.appointment
                                            .filter((a) => moment(a.start).format('YYYY-MM-DD') == moment(daysInWeek[index].date).format('YYYY-MM-DD'))
                                            ?.map((data, dataIndex) => {
                                                return (
                                                    <Card
                                                        key={dataIndex}
                                                        className="customeCalendarCardEvent2"
                                                        style={{
                                                            backgroundColor: backgroundColor(data?.status),
                                                            borderLeft: borderColor(data?.status),
                                                            cursor: 'pointer',
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleMouseLeave();
                                                            props.setSelectedAppointmentId(data?.appointmentId);
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.stopPropagation();
                                                            const appointmentData = {
                                                                title: data?.title,
                                                                customer: data?.customer,
                                                                services: data?.services,
                                                                start: data?.start,
                                                                status: data?.status,
                                                                appointmentId: data?.appointmentId,
                                                                vehicle: data?.customer?.vehicle || data?.vehicle,
                                                                noteForCustomer: data?.noteForCustomer,
                                                                note: data?.note,
                                                            };
                                                            handleMouseEnter(e, appointmentData);
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.stopPropagation();
                                                            handleMouseLeave();
                                                        }}
                                                    >
                                                        <Box style={styles.oneCard}>
                                                            <h3 style={{ marginBottom: 'auto' }}>
                                                                {dayjs(data?.start).format('h:mm a') + ' - '}
                                                                {data?.customer?.firstName + ' ' + (data?.customer?.lastName ?? '')}
                                                            </h3>
                                                        </Box>
                                                    </Card>
                                                );
                                            })}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                )}
                {(props.view == '3day' || props.view == '5day') && (props.resources.length == 1 || props.resources.length == 0) && (
                    <TableBody>
                        {slotData.map((row, rowIndex) => (
                            <TableRow key={row.name} ref={(el) => addRef(el, rowIndex)}>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    style={{
                                        ...styles.tdSlotHeading,
                                        borderBottom: rowIndex == slotData.length - 1 ? '1px solid #D8D8D8' : 'none',
                                    }}
                                    key={row.time}
                                    onClick={() => {
                                        if (hoverDialog.open) {
                                            handleCloseHoverDialog();
                                        }
                                    }}
                                >
                                    <Typography
                                        style={{
                                            marginBottom: '-20px',
                                            display: slotData.length == rowIndex + 1 && 'none',
                                        }}
                                    >
                                        <div style={styles.slotTime}>
                                            <div>{row.time}</div>
                                            <div>
                                                <hr style={{ width: '12px' }} />
                                            </div>
                                        </div>
                                    </Typography>
                                </TableCell>
                                {row?.appointment?.map((dd, mainIndex) => {
                                    if (mainIndex < dayLimit - 1)
                                        return (
                                            <TableCell
                                                style={styles.td}
                                                key={mainIndex}
                                                onClick={() => {
                                                    if (hoverDialog.open) {
                                                        handleCloseHoverDialog();
                                                    }
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: dayLimit == 6 ? '36%' : '60%',
                                                        display: 'flex',
                                                        position: 'absolute',
                                                    }}
                                                >
                                                    {row?.appointment[mainIndex]?.map((d, index) => {
                                                        // Find the actual event data to pass complete info
                                                        const eventInfo = props.events.find((e) => e.appointmentId === d.id);

                                                        return (
                                                            <Card
                                                                key={index}
                                                                className="customeCalendarCardEvent"
                                                                style={{
                                                                    display: row?.appointment[mainIndex][index].number != '' ? '' : 'none',
                                                                    textAlign: 'left',
                                                                    backgroundColor: backgroundColor(row?.appointment[mainIndex][index]?.appointmentStatus),
                                                                    borderLeft: borderColor(row?.appointment[mainIndex][index]?.appointmentStatus),
                                                                    height: row.appointment[mainIndex][index].height + 'px',
                                                                    width: 45 / (row?.appointment[mainIndex].length - 1) + '%',
                                                                    position: 'relative',
                                                                    marginTop:
                                                                        row.appointment[mainIndex][index].hasMargin && row.appointment[mainIndex][index].margin,
                                                                    cursor: 'pointer',
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleMouseLeave();
                                                                    props.setSelectedAppointmentId(row?.appointment[mainIndex][index].id);
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.stopPropagation();
                                                                    if (eventInfo) {
                                                                        const appointmentData = {
                                                                            title: eventInfo?.title,
                                                                            customer: eventInfo?.customer,
                                                                            services: eventInfo?.services || [],
                                                                            start: eventInfo?.start,
                                                                            status: eventInfo?.status,
                                                                            appointmentId: eventInfo?.appointmentId,
                                                                            vehicle: eventInfo?.customer?.vehicle || eventInfo?.vehicle,
                                                                            noteForCustomer: eventInfo?.noteForCustomer,
                                                                            note: eventInfo?.note,
                                                                        };
                                                                        handleMouseEnter(e, appointmentData);
                                                                    }
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.stopPropagation();
                                                                    handleMouseLeave();
                                                                }}
                                                            >
                                                                <Box style={styles.oneCard}>
                                                                    <h3 style={{ marginBottom: 'auto' }}>
                                                                        {row?.appointment[mainIndex][index].number + ' '}
                                                                        {row?.appointment[mainIndex][index].customerName}
                                                                    </h3>
                                                                    {row?.appointment[mainIndex][index].dropOffTime != '' && 'Drop Off :'}
                                                                    {row?.appointment[mainIndex][index].dropOffTime}
                                                                    <br />
                                                                    {row?.appointment[mainIndex][index].service}
                                                                </Box>
                                                            </Card>
                                                        );
                                                    })}
                                                </div>
                                            </TableCell>
                                        );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                )}
            </Table>

            {/* Hover Dialog */}
            {hoverDialog.open && (
                <AppointmentHoverDialog
                    open={hoverDialog.open}
                    onClose={handleCloseHoverDialog}
                    appointment={hoverDialog.appointment}
                    position={hoverDialog.position}
                    onMouseEnter={handleDialogMouseEnter}
                    onMouseLeave={handleDialogMouseLeave}
                    onMeasureHeight={(h) => {
                        if (!h || !lastTriggerRectRef.current) return;
                        measuredDialogHeightRef.current = h;
                        const { x, y } = computeFixedPosition(lastTriggerRectRef.current, 300, h);
                        setHoverDialog((prev) => ({ ...prev, position: { x, y } }));
                    }}
                />
            )}
        </div>
    );
};

export default CustomCalendar;
